import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { Appointment } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import { Provider } from '@weave/schema-gen-ts/dist/schemas/schedule/settings/v2/settings.pb';
import { ContactType_Enum, PersonStatus_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import dayjs from 'dayjs';
import { ScheduleTypes } from '@frontend/api-schedule';
import { titleCase } from '@frontend/string';
import { getFullName } from '../../utils';
import { TransformedLegacyPerson } from './components/PersonSelector/types';
import { getPersonContactInfoByType } from './utils';

const FILTERED_PROVIDERS_IN_CALENDAR_VIEW_LOCAL_STORAGE_KEY = 'FilteredProvidersInCalendarView';

export const getFilteredProvidersInCalendarViewLocationStorageKey = (locationId: string, userId: string) => {
  return `${FILTERED_PROVIDERS_IN_CALENDAR_VIEW_LOCAL_STORAGE_KEY}_${locationId}_${userId}`;
};

export const transformToLegacyPerson = (person: Person) => {
  return {
    PreferredName: person.preferredName ?? '',
    FirstName: person.firstName ?? '',
    LastName: person.lastName ?? '',
    PersonID: person.personId,
    ContactInfo: person.contactInfo,
    Notes: person.notes ?? '',
    SourceID: person.sourceId ?? '',
    SourceTenantID: person.sourceTenantId ?? '',
    MobilePhone: getPersonContactInfoByType({
      contactInfo: person?.contactInfo,
      contactType: ContactType_Enum.PHONE_MOBILE,
    }),
    Email: getPersonContactInfoByType({ contactInfo: person?.contactInfo, contactType: ContactType_Enum.EMAIL }),
    HomePhone: getPersonContactInfoByType({
      contactInfo: person?.contactInfo,
      contactType: ContactType_Enum.PHONE_HOME,
    }),
    PMID: person.personPmid ?? '',
    PMDisplayId: person.personDisplayPmid ?? '',
    Gender: person.gender ?? '',
    Status: titleCase(PersonStatus_Enum[person.status || PersonStatus_Enum.UNSPECIFIED]),
    Birthdate: person.birthdate ?? '',
  } as unknown as TransformedLegacyPerson;
};

type ExtendedAppointment = Appointment & { appointmentTypeId?: string };

type GetFilteredAppointmentsProps = {
  appointmentsToFilter: ExtendedAppointment[];
  providerList: Provider[];
  selectedAppointmentTypes?: string[];
  appointmentStatus: string;
  selectedOperatoryList: string[];
};

export const getFilteredAppointments = ({
  appointmentsToFilter,
  providerList,
  selectedAppointmentTypes,
  appointmentStatus,
  selectedOperatoryList,
}: GetFilteredAppointmentsProps): Appointment[] => {
  if (!appointmentsToFilter.length) return [];

  if (!providerList.length && !selectedAppointmentTypes?.length && !appointmentStatus && !selectedOperatoryList.length)
    return appointmentsToFilter;

  return appointmentsToFilter
    ?.filter((appointment) => {
      if (appointment.appointmentTypeId) {
        // When the appointment has the appointment type id, use it instead of the name. This will be present in V3 only. The appointment type id is named referenceTypeId in the V3 event data. When transforming the V3 event to a V2 appointment, we are instead naming it appointmentTypeId for clarity.
        return !!selectedAppointmentTypes?.length
          ? selectedAppointmentTypes?.includes(appointment.appointmentTypeId ?? '')
          : true;
      }
      return !!selectedAppointmentTypes?.length ? selectedAppointmentTypes.includes(appointment.type ?? '') : true;
    })
    ?.filter((appointment) => {
      return !!selectedOperatoryList?.length ? selectedOperatoryList.includes(appointment.operatory ?? '') : true;
    })
    .filter((appointment) => {
      return !!appointmentStatus && appointmentStatus !== 'all'
        ? appointment.statusOfficeView?.toUpperCase().replaceAll('_', ' ') ===
            appointmentStatus.toUpperCase().replaceAll('_', ' ')
        : true;
    })
    .filter((appointment) => {
      if (!providerList.length) return true;
      return providerList.some((provider) => {
        const appointmentPractitionerName = appointment.practitionerName?.trim();
        const providerPublicDisplayName = provider.publicDisplayName?.trim();
        const providerFullName = getFullName(provider);
        const providerResourceName = provider.resourceName?.trim();

        const hasMatchingProvider =
          (appointmentPractitionerName === providerPublicDisplayName ||
            appointmentPractitionerName === providerFullName ||
            appointmentPractitionerName === providerResourceName) &&
          appointment.locationId === provider.locationId;

        const hasUnassignedProvider =
          !appointmentPractitionerName &&
          !!appointment.providerIds?.length &&
          appointment?.providerIds[0] === '' &&
          provider.id === `unassigned-${appointment.locationId}` &&
          providerPublicDisplayName === 'unassigned';

        return hasMatchingProvider || hasUnassignedProvider;
      });
    });
};

export const getLocalStorageProviderListDetails = (
  locationId: string,
  userId: string,
  defaultProvidersList?: Provider[]
) => {
  try {
    const localStorageProviderIdsList: Array<string> = JSON.parse(
      localStorage.getItem(getFilteredProvidersInCalendarViewLocationStorageKey(locationId, userId)) || '[]'
    );

    const localStorageProviderList =
      defaultProvidersList?.filter((provider) => provider.id && localStorageProviderIdsList.includes(provider.id)) ||
      [];

    return {
      localStorageProviderIdsList,
      localStorageProviderList,
    };
  } catch (err) {
    console.error(err);
    return {
      localStorageProviderIdsList: [],
      localStorageProviderList: [],
    };
  }
};

export const getUniqueOperatoryList = (locationIds: string[], defaultOperatoryList: ScheduleTypes.OperatoryType[]) => {
  const uniqueOperatories = new Map();
  // Remove duplicate operatories across all locations
  defaultOperatoryList.forEach((operatory) => {
    if (!operatory.value) return;
    const uniqueKey = `${operatory.key}-${operatory.display_name}`;
    // Check if the operatory is already in the map or if it belongs to the specified location. If it belongs to the specified location, we can add it even if it's a duplicate in a non-selected location.
    if (!uniqueOperatories.has(uniqueKey) || locationIds.includes(operatory.locationId)) {
      uniqueOperatories.set(uniqueKey, operatory);
    }
  });
  return Array.from(uniqueOperatories.values());
};

type GetIsValidEndTimeProps = {
  date: string;
  startTime: string;
  endTime: string;
};

export const isValidEndTime = ({ date, startTime, endTime }: GetIsValidEndTimeProps): boolean => {
  if (!date || !startTime || !endTime) return true;
  // Parse the date and time strings into dayjs objects
  const startDate = dayjs(`${date} ${startTime}`);
  const endDate = dayjs(`${date} ${endTime}`);

  // Check if the end time is before or the same as the start time
  if (endDate.isBefore(startDate) || endDate.isSame(startDate)) {
    return false;
  }

  return true;
};
